import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, ArrowLeft, CheckCircle2, Circle, MessageSquare,
  Calendar, AlertOctagon, Send, RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import { StaffHeader, StaffBottomNav } from '@/components/staff/StaffNav';
import {
  meTasks, meMarkTaskDone, meReopenTask, mePostTaskMessage, meMarkTaskRead, fmtErr,
} from '@/lib/staffApi';

const PRIORITY_LABEL = {
  1: 'ASAP', 2: 'Deadline', 3: 'On date', 4: 'Window', 5: 'When free',
};
const PRIORITY_TONE = {
  1: 'bg-red-100 text-red-800 border-red-200',
  2: 'bg-orange-100 text-orange-800 border-orange-200',
  3: 'bg-amber-100 text-amber-800 border-amber-200',
  4: 'bg-sky-100 text-sky-800 border-sky-200',
  5: 'bg-muted text-muted-foreground border-border',
};

function fmtDateTime(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
}
function fmtDate(s) {
  if (!s) return '';
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
function priorityDateLine(t) {
  if (t.priority === 2 && t.due_at) return `Due ${fmtDateTime(t.due_at)}`;
  if (t.priority === 3 && t.due_date) return `Do on ${fmtDate(t.due_date)}`;
  if (t.priority === 4 && (t.start_date || t.end_date)) return `${fmtDate(t.start_date)} – ${fmtDate(t.end_date)}`;
  return '';
}

export function StaffTasksPage() {
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState({ open: [], done: [] });
  const [selected, setSelected] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try { setTasks(await meTasks()); }
    catch (e) { toast.error(fmtErr(e, 'Failed to load tasks')); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const open = async (task) => {
    setSelected(task);
    if (task.unread_for_assignee) {
      try { await meMarkTaskRead(task.id); refresh(); } catch {}
    }
  };

  return (
    <div className="min-h-screen bg-[#fbfaf7]" data-testid="staff-tasks-page">
      <StaffHeader title="My tasks" />
      <main className="max-w-2xl mx-auto px-4 py-5 pb-24 space-y-4">
        <Link to="/staff/home" className="text-xs text-muted-foreground hover:text-[#1f2933] inline-flex items-center gap-1" data-testid="tasks-back-home">
          <ArrowLeft className="h-3 w-3" /> Home
        </Link>

        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div>
        ) : tasks.open.length === 0 && tasks.done.length === 0 ? (
          <p className="text-sm text-muted-foreground py-12 text-center">No tasks assigned to you yet.</p>
        ) : (
          <>
            {tasks.open.length > 0 && (
              <section>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2">To do ({tasks.open.length})</p>
                <div className="space-y-2">
                  {tasks.open.map(t => <StaffTaskRow key={t.id} task={t} onSelect={open} />)}
                </div>
              </section>
            )}
            {tasks.done.length > 0 && (
              <section>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2 mt-4">Done ({tasks.done.length})</p>
                <div className="space-y-2 opacity-75">
                  {tasks.done.map(t => <StaffTaskRow key={t.id} task={t} onSelect={open} />)}
                </div>
              </section>
            )}
          </>
        )}
      </main>
      <StaffBottomNav />
      <StaffTaskSheet task={selected} onClose={() => setSelected(null)} onRefresh={refresh} />
    </div>
  );
}

function StaffTaskRow({ task, onSelect }) {
  const dateLine = priorityDateLine(task);
  return (
    <Card
      className={`cursor-pointer hover:shadow-sm transition-shadow ${task.unread_for_assignee ? 'ring-1 ring-emerald-300' : ''}`}
      onClick={() => onSelect(task)}
      data-testid={`staff-task-${task.id}`}
    >
      <div className="flex items-start gap-3 px-3 py-3">
        {task.status === 'done'
          ? <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
          : <Circle className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />}
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${task.status === 'done' ? 'line-through text-muted-foreground' : 'text-[#1f2933]'}`}>{task.title}</p>
          <div className="flex flex-wrap items-center gap-1.5 mt-1">
            <Badge className={`${PRIORITY_TONE[task.priority]} text-[10px]`}>P{task.priority} · {PRIORITY_LABEL[task.priority]}</Badge>
            {task.project_name && <span className="text-[11px] text-muted-foreground">{task.project_name}</span>}
          </div>
          {dateLine && <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1"><Calendar className="h-2.5 w-2.5" /> {dateLine}</p>}
        </div>
        {(task.thread?.length > 0) && (
          <span className="text-[11px] text-muted-foreground flex items-center gap-1">
            <MessageSquare className="h-3 w-3" /> {task.thread.length}
          </span>
        )}
        {task.unread_for_assignee && <span className="h-2 w-2 bg-emerald-500 rounded-full" title="Unread" />}
      </div>
    </Card>
  );
}

export function StaffTaskSheet({ task, onClose, onRefresh }) {
  const [doneNote, setDoneNote] = useState('');
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { setDoneNote(''); setReply(''); }, [task?.id]);

  if (!task) return null;
  const dateLine = priorityDateLine(task);

  const markDone = async () => {
    setBusy(true);
    try {
      await meMarkTaskDone(task.id, doneNote);
      toast.success('Marked done. Admin will see this in their notifications.');
      onRefresh && onRefresh();
      onClose();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };
  const reopen = async () => {
    setBusy(true);
    try { await meReopenTask(task.id); toast.success('Reopened'); onRefresh && onRefresh(); onClose(); }
    catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };
  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await mePostTaskMessage(task.id, reply.trim());
      setReply('');
      onRefresh && onRefresh();
      toast.success('Sent — admin will be notified');
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-6" onClick={onClose} data-testid="staff-task-sheet">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b flex items-center gap-2">
          {task.status === 'done'
            ? <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            : <AlertOctagon className="h-5 w-5 text-amber-600" />}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-[#1f2933] truncate">{task.title}</p>
            <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
              <Badge className={`${PRIORITY_TONE[task.priority]} text-[10px]`}>P{task.priority} · {PRIORITY_LABEL[task.priority]}</Badge>
              {task.project_id && task.project_name && (
                <button
                  type="button"
                  onClick={() => navigate(`/staff/projects/${task.project_id}`)}
                  className="text-[11px] text-emerald-700 hover:underline"
                  data-testid="staff-task-open-project"
                >
                  Open {task.project_name} →
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="p-4 space-y-3">
          {dateLine && <p className="text-xs text-[#1f2933] flex items-center gap-1.5"><Calendar className="h-3 w-3" /> {dateLine}</p>}
          {task.description && (
            <div className="rounded-md bg-muted/40 p-3 text-sm whitespace-pre-wrap">{task.description}</div>
          )}

          {task.status !== 'done' ? (
            <div className="rounded-md border p-3 space-y-2 bg-emerald-50/30 border-emerald-200">
              <p className="text-xs font-semibold text-[#1f2933]">Done? Add a note (optional) and mark complete.</p>
              <Textarea
                value={doneNote}
                onChange={e => setDoneNote(e.target.value)}
                placeholder="What did you do? Anything the admin should know?"
                rows={2}
                className="text-sm bg-white"
                data-testid="staff-task-done-note"
              />
              <Button size="sm" onClick={markDone} disabled={busy} className="w-full" data-testid="staff-task-done-btn">
                {busy ? <Loader2 className="h-3 w-3 animate-spin mr-1.5" /> : <CheckCircle2 className="h-3 w-3 mr-1.5" />}
                Mark done
              </Button>
            </div>
          ) : (
            <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-3 space-y-2">
              <p className="text-xs font-semibold text-emerald-800 flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3" />Marked done {fmtDateTime(task.done_at)}</p>
              {task.done_note && <p className="text-xs whitespace-pre-wrap">{task.done_note}</p>}
              <Button size="sm" variant="outline" onClick={reopen} disabled={busy} className="w-full" data-testid="staff-task-reopen-btn">
                <RotateCcw className="h-3 w-3 mr-1.5" /> Reopen
              </Button>
            </div>
          )}

          <div className="space-y-2">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Messages with admin</p>
            {(task.thread || []).length === 0 && <p className="text-xs text-muted-foreground italic">Start a thread below…</p>}
            {(task.thread || []).map(m => (
              <div key={m.id} className={`rounded-md p-2 text-sm ${m.author_role === 'admin' ? 'bg-emerald-50 border border-emerald-100' : 'bg-blue-50 border border-blue-100'}`} data-testid={`staff-thread-msg-${m.id}`}>
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <span className="text-[11px] font-medium">{m.author_name} <span className="opacity-60">({m.author_role})</span></span>
                  <span className="text-[10px] text-muted-foreground">{fmtDateTime(m.created_at)}</span>
                </div>
                <p className="whitespace-pre-wrap">{m.body}</p>
              </div>
            ))}
          </div>

          <div className="flex items-end gap-2 pt-2 border-t">
            <Textarea
              value={reply}
              onChange={e => setReply(e.target.value)}
              placeholder="Message the admin…"
              rows={2}
              className="flex-1 text-sm"
              data-testid="staff-task-reply"
            />
            <Button size="sm" onClick={send} disabled={busy || !reply.trim()} data-testid="staff-task-send">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
            </Button>
          </div>
          <Button size="sm" variant="outline" className="w-full" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}
